import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Group,
  Map as MapIcon,
  MoreVert,
  Star,
  Sync,
  ViewList,
  ViewModule
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dataverseService } from '../services/dataverseService';
import {
  getDataverseErrorMessage,
  handleDataverseLoadError
} from '../lib/dataverseErrorHandler';
import { ACCESS_GROUP_SYNC_STATUS_LABELS } from '../config/accessGroupSync';
import { getCustomerById, getCustomerMapCenter } from '../config/customers';
import { getResolvedCustomerId } from '../config/environment';
import { getCustomerFromCurrentHostname } from '../lib/customerFromHostname';
import { getSelectedCustomer } from '../lib/selectedCustomer';
import CreateAccessGroupFromMapDialog from '../components/CreateAccessGroupFromMapDialog';
import AccessGroupZoneDialog from '../components/AccessGroupZoneDialog';

const emptyForm = {
  name: '',
  description: '',
  defaultForLocks: false,
  defaultForPhones: false
};

function AccessGroupMetaChips({ group }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {group.defaultForLocks && (
        <Chip icon={<Star />} size="small" label="Default låser" color="primary" variant="outlined" />
      )}
      {group.defaultForPhones && (
        <Chip icon={<Star />} size="small" label="Default telefoner" color="secondary" variant="outlined" />
      )}
      {group.mapPolygon && (
        <Chip icon={<MapIcon />} size="small" label="Kart-sone" color="info" variant="outlined" />
      )}
      {group.unlocId ? (
        <Chip size="small" label="Synket til Unloc" color="success" variant="outlined" />
      ) : (
        <Chip size="small" label="Venter på Unloc-opprettelse" color="warning" variant="outlined" />
      )}
    </Box>
  );
}

function AccessGroupCard({ group, onOpenMenu }) {
  return (
    <Card className="card-hover">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Group color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {group.name}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={`Handlinger for tilgangsgruppe ${group.name}`}
            onClick={(event) => onOpenMenu(event, group)}
          >
            <MoreVert />
          </IconButton>
        </Box>

        {group.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {group.description}
          </Typography>
        )}

        <Box sx={{ mb: 1.5 }}>
          <AccessGroupMetaChips group={group} />
        </Box>

        {group.lastSyncedOn && (
          <Typography variant="caption" color="text.secondary" display="block">
            Sist synket: {new Date(group.lastSyncedOn).toLocaleString('nb-NO')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function AccessGroupRow({ group, onOpenMenu, onMembers, onEdit }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, flex: '1 1 180px' }}>
        <Group color="primary" fontSize="small" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {group.name}
          </Typography>
          {group.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {group.description}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: '2 1 220px', minWidth: 160 }}>
        <AccessGroupMetaChips group={group} />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 140px', minWidth: 120 }}>
        {group.lastSyncedOn
          ? `Synket: ${new Date(group.lastSyncedOn).toLocaleString('nb-NO')}`
          : 'Ikke synket'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, ml: 'auto', alignItems: 'center' }}>
        <Button size="small" startIcon={<Sync />} onClick={() => onMembers(group)}>
          Medlemskap
        </Button>
        <Button size="small" startIcon={<Edit />} onClick={() => onEdit(group)}>
          Rediger
        </Button>
        <IconButton
          size="small"
          aria-label={`Handlinger for tilgangsgruppe ${group.name}`}
          onClick={(event) => onOpenMenu(event, group)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

const AccessGroupsPage = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [locks, setLocks] = useState([]);
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedLockIds, setSelectedLockIds] = useState([]);
  const [selectedPhoneIds, setSelectedPhoneIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuGroup, setMenuGroup] = useState(null);
  const [recentSyncs, setRecentSyncs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [pullingFromUnloc, setPullingFromUnloc] = useState(false);
  const [mapCreateOpen, setMapCreateOpen] = useState(false);
  const [zoneGroup, setZoneGroup] = useState(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const mapCenter = useMemo(
    () =>
      getCustomerMapCenter(
        getCustomerFromCurrentHostname() ||
          getSelectedCustomer() ||
          getCustomerById(getResolvedCustomerId())
      ),
    []
  );

  const unlocLocks = useMemo(
    () => locks.filter((lock) => lock.provider === 'unloc' || lock.externalId),
    [locks]
  );

  const loadData = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const [groupData, lockData, phoneData] = await Promise.all([
        dataverseService.accessGroupService.getAll(),
        dataverseService.lockService.getAll(),
        dataverseService.phoneService.getAll()
      ]);
      setGroups(groupData);
      setLocks(lockData);
      setPhones(phoneData);
    } catch (loadError) {
      console.error('Error loading access groups:', loadError);
      if (handleDataverseLoadError(loadError, navigate)) {
        return;
      }
      setGroups([]);
      setError(getDataverseErrorMessage(loadError));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openZoneDialog = async (group) => {
    try {
      setSaving(true);
      const full = await dataverseService.accessGroupService.getById(group.id);
      if (!full.mapPolygon && group.mapPolygon) {
        full.mapPolygon = group.mapPolygon;
      }
      if (!full.mapPolygon) {
        setError('Denne gruppen har ingen lagret kart-sone.');
        return;
      }
      setZoneGroup(full);
    } catch (loadError) {
      setError(getDataverseErrorMessage(loadError));
    } finally {
      setSaving(false);
      setMenuAnchor(null);
      setMenuGroup(null);
    }
  };

  const openCreateDialog = () => {
    setEditingGroup(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      defaultForLocks: group.defaultForLocks,
      defaultForPhones: group.defaultForPhones
    });
    setDialogOpen(true);
    setMenuAnchor(null);
    setMenuGroup(null);
  };

  const applyMembershipSelection = (full) => {
    setSelectedGroup(full);
    setSelectedLockIds(
      full.locks.map((lock) => String(lock.id || lock.lockId || '').toLowerCase()).filter(Boolean)
    );
    setSelectedPhoneIds(
      full.phones.map((phone) => String(phone.id || phone.phoneId || '').toLowerCase()).filter(Boolean)
    );
  };

  const pullMembershipFromUnloc = async (group) => {
    if (!group?.id || !group?.unlocId || pullingFromUnloc) {
      return null;
    }

    setPullingFromUnloc(true);
    setSyncError(null);
    setError(null);
    setSyncMessage(`Henter medlemskap for «${group.name}» fra Unloc…`);
    try {
      const updated = await dataverseService.accessGroupService.syncMembershipFromUnloc(group.id);
      applyMembershipSelection(updated);
      setSyncMessage(`Medlemskap for «${updated.name || group.name}» oppdatert fra Unloc`);
      await loadData({ showLoading: false });
      return updated;
    } catch (pullError) {
      setSyncError(getDataverseErrorMessage(pullError, 'Kunne ikke hente medlemskap fra Unloc.'));
      setSyncMessage(null);
      return null;
    } finally {
      setPullingFromUnloc(false);
    }
  };

  const openMembersDialog = async (group) => {
    try {
      setSaving(true);
      const full = await dataverseService.accessGroupService.getById(group.id);
      applyMembershipSelection(full);
      setShowSelectedOnly(false);
      const syncs = await dataverseService.accessGroupService.syncService.listByGroup(group.id, {
        top: 5
      });
      setRecentSyncs(syncs);
      setMembersDialogOpen(true);
      setSaving(false);
      setMenuAnchor(null);
      setMenuGroup(null);

      // Always refresh membership from Unloc when opening (if group is linked)
      if (full.unlocId) {
        await pullMembershipFromUnloc(full);
        const refreshedSyncs = await dataverseService.accessGroupService.syncService.listByGroup(
          group.id,
          { top: 5 }
        );
        setRecentSyncs(refreshedSyncs);
      }
    } catch (loadError) {
      setError(getDataverseErrorMessage(loadError));
      setSaving(false);
      setMenuAnchor(null);
      setMenuGroup(null);
    }
  };

  const handleSaveGroup = async () => {
    if (!formData.name.trim()) {
      setError('Navn er påkrevd.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingGroup) {
        await dataverseService.accessGroupService.update(editingGroup.id, {
          name: formData.name,
          description: formData.description,
          defaultForLocks: formData.defaultForLocks,
          defaultForPhones: formData.defaultForPhones
        });
      } else {
        await dataverseService.accessGroupService.create({
          name: formData.name,
          description: formData.description,
          defaultForLocks: formData.defaultForLocks,
          defaultForPhones: formData.defaultForPhones
        });
      }

      setDialogOpen(false);
      await loadData();
    } catch (saveError) {
      setError(getDataverseErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMembers = async () => {
    if (!selectedGroup) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await dataverseService.accessGroupService.setMembership(selectedGroup.id, {
        lockIds: selectedLockIds,
        phoneIds: selectedPhoneIds
      });
      setMembersDialogOpen(false);
      await loadData();
    } catch (saveError) {
      setError(getDataverseErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFromUnloc = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    setSyncError(null);
    setError(null);
    setSyncMessage('Starter synk fra Unloc…');

    try {
      setSyncMessage('Henter tilgangsgrupper fra Unloc…');
      const summary = await dataverseService.accessGroupService.importFromUnloc();
      setSyncMessage(
        `Oppdatert fra Unloc: ${summary.imported} nye, ${summary.linked} koblet, ${summary.synced} synket.`
      );
      await loadData({ showLoading: false });
    } catch (importError) {
      console.error('Access group sync failed:', importError);
      setSyncError(
        getDataverseErrorMessage(importError, 'Kunne ikke oppdatere tilgangsgrupper fra Unloc.')
      );
      setSyncMessage(null);
    } finally {
      setSyncing(false);
    }
  };

  const handlePullMembershipFromUnloc = async () => {
    if (!selectedGroup) {
      return;
    }
    await pullMembershipFromUnloc(selectedGroup);
  };

  const handleDeactivate = async (group) => {
    if (!window.confirm(`Deaktiver tilgangsgruppen «${group.name}»?`)) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await dataverseService.accessGroupService.deactivate(group.id);
      await loadData();
    } catch (deactivateError) {
      setError(getDataverseErrorMessage(deactivateError));
    } finally {
      setSaving(false);
      setMenuAnchor(null);
      setMenuGroup(null);
    }
  };

  const toggleSelection = (id, selected, setSelected) => {
    const key = String(id || '').toLowerCase();
    if (!key) {
      return;
    }
    if (selected.includes(key)) {
      setSelected(selected.filter((item) => item !== key));
    } else {
      setSelected([...selected, key]);
    }
  };

  const isLockSelected = (lock) => {
    const key = String(lock.lockId || lock.id || '').toLowerCase();
    return selectedLockIds.includes(key);
  };

  const isPhoneSelected = (phone) => {
    const key = String(phone.id || phone.phoneId || '').toLowerCase();
    return selectedPhoneIds.includes(key);
  };

  const visibleLocks = showSelectedOnly
    ? unlocLocks.filter((lock) => isLockSelected(lock))
    : unlocLocks;

  const visiblePhones = showSelectedOnly
    ? phones.filter((phone) => isPhoneSelected(phone))
    : phones;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="page-container">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Tilgangsgrupper
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Grupper av låser og telefoner som synkroniseres til Unloc Access Groups. Nøkler opprettes
            automatisk for hver kombinasjon telefon × lås i gruppen.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <Sync />}
            onClick={handleSyncFromUnloc}
            disabled={syncing || saving || loading}
          >
            {syncing ? 'Oppdaterer…' : 'Oppdater fra Unloc'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={() => setMapCreateOpen(true)}
            disabled={saving || loading}
          >
            Opprett fra kart
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            Ny gruppe
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, next) => {
              if (next) setViewMode(next);
            }}
          >
            <ToggleButton value="list">
              <ViewList sx={{ mr: 0.75 }} />
              Liste
            </ToggleButton>
            <ToggleButton value="tiles">
              <ViewModule sx={{ mr: 0.75 }} />
              Kort
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {syncError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncError(null)}>
          {syncError}
        </Alert>
      )}

      {syncMessage && !syncError && (
        <Alert
          severity={syncing || pullingFromUnloc ? 'info' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => !syncing && !pullingFromUnloc && setSyncMessage(null)}
        >
          {syncMessage}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Gruppenøkler fjernes kun ved å ta lås eller telefon ut av gruppen — ikke via vanlig
        «Trekk tilbake». Direkte nøkler og gruppenøkler kan eksistere parallelt. Endringer gjort
        direkte i Unloc. Medlemskap hentes automatisk fra Unloc når du åpner en gruppe (kan også kjøres
        manuelt med «Hent fra Unloc»).
      </Alert>

      {groups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              Ingen tilgangsgrupper ennå. Opprett en gruppe for å samle låser og telefoner.
            </Typography>
          </CardContent>
        </Card>
      ) : viewMode === 'tiles' ? (
        <Grid container spacing={2}>
          {groups.map((group) => (
            <Grid item xs={12} md={6} lg={4} key={group.id}>
              <AccessGroupCard
                group={group}
                onOpenMenu={(event, selected) => {
                  setMenuAnchor(event.currentTarget);
                  setMenuGroup(selected);
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {groups.map((group) => (
            <AccessGroupRow
              key={group.id}
              group={group}
              onOpenMenu={(event, selected) => {
                setMenuAnchor(event.currentTarget);
                setMenuGroup(selected);
              }}
              onMembers={openMembersDialog}
              onEdit={openEditDialog}
            />
          ))}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => openMembersDialog(menuGroup)}>
          <Sync sx={{ mr: 1 }} fontSize="small" />
          Medlemskap
        </MenuItem>
        <MenuItem
          disabled={!menuGroup?.mapPolygon}
          onClick={() => openZoneDialog(menuGroup)}
        >
          <MapIcon sx={{ mr: 1 }} fontSize="small" />
          Kart-sone
        </MenuItem>
        <MenuItem onClick={() => openEditDialog(menuGroup)}>
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Rediger
        </MenuItem>
        <MenuItem onClick={() => handleDeactivate(menuGroup)}>
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Deaktiver
        </MenuItem>
      </Menu>

      <CreateAccessGroupFromMapDialog
        open={mapCreateOpen}
        onClose={() => setMapCreateOpen(false)}
        locks={unlocLocks}
        center={mapCenter}
        onCreated={() => loadData({ showLoading: false })}
      />

      <AccessGroupZoneDialog
        open={Boolean(zoneGroup)}
        onClose={() => setZoneGroup(null)}
        group={zoneGroup}
        locks={unlocLocks}
        center={mapCenter}
        onUpdated={async () => {
          await loadData({ showLoading: false });
          if (zoneGroup?.id) {
            try {
              const full = await dataverseService.accessGroupService.getById(zoneGroup.id);
              setZoneGroup({
                ...full,
                mapPolygon: full.mapPolygon || zoneGroup.mapPolygon
              });
            } catch {
              // keep previous zone dialog state
            }
          }
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Rediger tilgangsgruppe' : 'Ny tilgangsgruppe'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Navn"
            fullWidth
            value={formData.name}
            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Beskrivelse"
            fullWidth
            multiline
            minRows={2}
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.defaultForLocks}
                onChange={(event) =>
                  setFormData({ ...formData, defaultForLocks: event.target.checked })
                }
              />
            }
            label="Default for nye låser (fremtidige fra Unloc-batch)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.defaultForPhones}
                onChange={(event) =>
                  setFormData({ ...formData, defaultForPhones: event.target.checked })
                }
              />
            }
            label="Default for nye telefoner"
          />
          {(formData.defaultForLocks || formData.defaultForPhones) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Flere grupper kan være default samtidig. Nye låser og telefoner legges i alle
              aktive default-grupper. Endringen gjelder kun fremtidige registreringer.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSaveGroup} disabled={saving}>
            {saving ? 'Lagrer…' : 'Lagre'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Medlemskap: {selectedGroup?.name}</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              mb: 1.5
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSelectedOnly}
                  onChange={(event) => setShowSelectedOnly(event.target.checked)}
                />
              }
              label={`Vis valgte (${selectedLockIds.length} låser, ${selectedPhoneIds.length} telefoner)`}
            />
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Låser (Unloc)
            {showSelectedOnly
              ? ` — ${visibleLocks.length} valgt`
              : ` — ${selectedLockIds.length} av ${unlocLocks.length} valgt`}
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {visibleLocks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                {showSelectedOnly ? 'Ingen valgte låser.' : 'Ingen Unloc-låser funnet.'}
              </Typography>
            ) : (
              visibleLocks.map((lock) => (
                <MenuItem
                  key={lock.lockId || lock.id}
                  dense
                  onClick={() =>
                    toggleSelection(lock.lockId || lock.id, selectedLockIds, setSelectedLockIds)
                  }
                >
                  <Checkbox checked={isLockSelected(lock)} />
                  <ListItemText primary={lock.name} secondary={lock.location || lock.externalId} />
                </MenuItem>
              ))
            )}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Telefoner
            {showSelectedOnly
              ? ` — ${visiblePhones.length} valgt`
              : ` — ${selectedPhoneIds.length} av ${phones.length} valgt`}
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {visiblePhones.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                {showSelectedOnly ? 'Ingen valgte telefoner.' : 'Ingen telefoner funnet.'}
              </Typography>
            ) : (
              visiblePhones.map((phone) => (
                <MenuItem
                  key={phone.id}
                  dense
                  onClick={() => toggleSelection(phone.id, selectedPhoneIds, setSelectedPhoneIds)}
                >
                  <Checkbox checked={isPhoneSelected(phone)} />
                  <ListItemText primary={phone.name} secondary={phone.mobileNumber} />
                </MenuItem>
              ))
            )}
          </Box>

          {recentSyncs.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Siste synk
              </Typography>
              {recentSyncs.map((sync) => (
                <Typography key={sync.id} variant="caption" display="block" color="text.secondary">
                  {new Date(sync.createdOn).toLocaleString('nb-NO')} —{' '}
                  {ACCESS_GROUP_SYNC_STATUS_LABELS[sync.status] || sync.status}
                  {sync.errorMessage ? `: ${sync.errorMessage}` : ''}
                </Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={pullingFromUnloc ? <CircularProgress size={18} /> : <Sync />}
            onClick={handlePullMembershipFromUnloc}
            disabled={saving || pullingFromUnloc || !selectedGroup?.unlocId}
          >
            Hent fra Unloc
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setMembersDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSaveMembers} disabled={saving}>
            {saving ? 'Lagrer…' : 'Lagre medlemskap'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessGroupsPage;
